import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, Settings, Trash2, Check, Save, Loader, ChevronLeft, ChevronRight, Stethoscope, Shield, MapPin } from 'lucide-react';
import html2canvas from 'html2canvas';
import { 
  saveAppointment,
  loadAppointments,
  loadAppointmentsFrom,
  deleteAppointment,
  saveSettings, 
  loadSettings,
  cleanOldAppointments,
  saveVaccination,
  loadVaccinations,
  deleteVaccination,
  saveBlockedDate,
  removeBlockedDate,
  loadBlockedDates
} from './firebase/operations';
import { formatDateLocal } from './utils/date';
import { buildAppointmentIcs, downloadIcs } from './utils/ics';

// Funzione per calcolare la Pasqua
const getEasterDate = (year) => {
  const f = Math.floor;
  const G = year % 19;
  const C = f(year / 100);
  const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
  const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
  const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
  const L = I - J;
  const month = 3 + f((L + 40) / 44);
  const day = L + 28 - 31 * f(month / 4);
  return new Date(year, month - 1, day);
};

// Funzione per controllare se una data è festiva in Italia
const isItalianHoliday = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JavaScript mesi 0-11, noi vogliamo 1-12
  const day = date.getDate();
  
  // Festività fisse
  const fixedHolidays = [
    { month: 1, day: 1 },   // Capodanno
    { month: 1, day: 6 },   // Epifania
    { month: 4, day: 25 },  // Festa della Liberazione
    { month: 5, day: 1 },   // Festa del Lavoro
    { month: 6, day: 2 },   // Festa della Repubblica
    { month: 8, day: 15 },  // Ferragosto
    { month: 11, day: 1 },  // Ognissanti
    { month: 12, day: 8 },  // Immacolata Concezione
    { month: 12, day: 25 }, // Natale
    { month: 12, day: 26 }  // Santo Stefano
  ];
  
  // Controlla festività fisse
  const isFixedHoliday = fixedHolidays.some(holiday => 
    holiday.month === month && holiday.day === day
  );
  
  if (isFixedHoliday) return true;
  
  // Festività mobili legate alla Pasqua
  const easter = getEasterDate(year);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);
  
  // Controlla se è Pasqua o Lunedì dell'Angelo
  if (date.toDateString() === easter.toDateString() || 
      date.toDateString() === easterMonday.toDateString()) {
    return true;
  }
  
  return false;
};
const PediatraBookingApp = () => {
  const [currentView, setCurrentView] = useState('welcome');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);
  const screenshotRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeAdminTab, setActiveAdminTab] = useState('appointments');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cameFromAdmin, setCameFromAdmin] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [showCalendarForm, setShowCalendarForm] = useState(false);
const [selectedCalendarSlot, setSelectedCalendarSlot] = useState(null);
const [calendarFormData, setCalendarFormData] = useState({
  nome: '',
  cognome: '',
  email: '',
  motivo: [],
  note: '',
  customTime: '',
  isPrivate: false
});  
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    motivo: [],
    note: ''
  });
  const [showForm, setShowForm] = useState(false);
  const [showVaccinationForm, setShowVaccinationForm] = useState(false);
  const [vaccinationFormData, setVaccinationFormData] = useState({
  nome: '',
  cognome: '',
  cellulare: '',
  primaVaccinazione: false
});
const [vaccinations, setVaccinations] = useState([]);
const [blockedDates, setBlockedDates] = useState([]);
  
  const defaultSettings = {
    appointmentDuration: 15,
    minAdvanceHours: 1,
    maxAdvanceDays: 180,
    weeklySchedule: {
      1: { available: true, slots: [{ start: '09:45', end: '11:00' }] },
      2: { available: true, slots: [{ start: '16:15', end: '17:45' }] },
      3: { available: true, slots: [{ start: '09:45', end: '11:00' }] },
      4: { available: true, slots: [{ start: '16:15', end: '17:45' }] },
      5: { available: true, slots: [{ start: '09:45', end: '11:00' }] },
      6: { available: false, slots: [] },
      0: { available: false, slots: [] }
    }
  };

  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAdmin = urlParams.get('admin') === 'true';

    const initApp = async () => {
      // AVVIO VELOCE: carica in parallelo solo ciò che serve per mostrare subito
      // la schermata. Gli appuntamenti sono limitati al mese corrente + futuro
      // (tutta la disponibilità che serve al paziente): niente più lettura
      // dell'intera collezione a ogni apertura.
      try {
        setLoading(true);
        console.log('Connessione a Firebase in corso...');

        const now = new Date();
        const startOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

        const [firebaseAppointments, firebaseSettings, firebaseBlockedDates] = await Promise.all([
          loadAppointmentsFrom(startOfMonth),
          loadSettings(),
          loadBlockedDates()
        ]);

        setAppointments(firebaseAppointments);
        if (firebaseSettings) {
          setSettings(firebaseSettings);
        }
        setBlockedDates(firebaseBlockedDates);

        console.log('Firebase connesso con successo');
      } catch (error) {
        console.error('Errore connessione Firebase:', error);
      } finally {
        setLoading(false);
      }

      // ATTIVITÀ IN BACKGROUND (non bloccano l'apertura, solo in area admin):
      // storico completo, vaccinazioni e pulizia (max 1 volta al giorno).
      if (isAdmin) {
        loadAppointments().then((all) => setAppointments(all)).catch(() => {});
        loadVaccinations().then((v) => setVaccinations(v)).catch(() => {});

        try {
          const lastCleanup = Number(localStorage.getItem('lastCleanup') || 0);
          if (Date.now() - lastCleanup > 24 * 60 * 60 * 1000) {
            cleanOldAppointments()
              .then(() => { try { localStorage.setItem('lastCleanup', String(Date.now())); } catch (e) {} })
              .catch(() => {});
          }
        } catch (e) {}
      }
    };

    initApp();

    if (isAdmin) {
      setCameFromAdmin(true);
    }
  }, []);

  // Funzione per generare e scaricare/condividere screenshot della conferma
  const generateScreenshot = useCallback(async () => {
    if (!screenshotRef.current) return;

    try {
      // Attendiamo un attimo per essere sicuri che il DOM sia renderizzato
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(screenshotRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Alta qualità
        logging: false,
        width: screenshotRef.current.offsetWidth,
        height: screenshotRef.current.offsetHeight
      });

      // Converti canvas in blob
      canvas.toBlob(async (blob) => {
        const fileName = `Prenotazione_${confirmedAppointment?.date}_${confirmedAppointment?.time?.replace(':', '')}.png`;

        // Rileva se siamo su mobile e se la Share API è supportata
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const canShare = navigator.share && navigator.canShare;

        if (isMobile && canShare) {
          try {
            // MOBILE: Usa Share API nativa
            const file = new File([blob], fileName, { type: 'image/png' });

            // Verifica se può condividere file
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: 'Prenotazione Confermata',
                text: `Appuntamento del ${confirmedAppointment?.dateFormatted} alle ${confirmedAppointment?.time}`
              });
              console.log('Screenshot condiviso con successo');
            } else {
              // Fallback a download se non può condividere file
              downloadFile(blob, fileName);
            }
          } catch (error) {
            // Se l'utente annulla la condivisione, non mostrare errore
            if (error.name === 'AbortError') {
              console.log('Condivisione annullata dall\'utente');
            } else {
              console.error('Errore condivisione:', error);
              // Fallback a download
              downloadFile(blob, fileName);
            }
          }
        } else {
          // DESKTOP o Share API non supportata: Download normale
          downloadFile(blob, fileName);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Errore generazione screenshot:', error);
      alert('Errore nella generazione dello screenshot. Riprova.');
    }
  }, [confirmedAppointment]);

  // Funzione helper per download tradizionale
  const downloadFile = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = fileName;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Genera screenshot automaticamente quando viene confermato un appuntamento
  useEffect(() => {
    if (currentView === 'success' && confirmedAppointment) {
      generateScreenshot();
    }
  }, [currentView, confirmedAppointment, generateScreenshot]);

  const handleSaveAppointment = async (appointmentData, options = {}) => {
    setSaving(true);
    const result = await saveAppointment(appointmentData, options);

    if (result.success) {
      const newAppointment = { id: result.id, ...appointmentData };
      setAppointments(prev => [...prev, newAppointment]);
    } else if (result.reason === 'slot_taken') {
      // Lo slot è stato prenotato da un altro paziente nel frattempo:
      // ricarico la lista così la UI mostra subito lo slot come occupato.
      const fresh = await loadAppointments();
      setAppointments(fresh);
    }
    setSaving(false);
    return result;
  };

  const handleDeleteAppointment = async (appointmentId) => {
    const success = await deleteAppointment(appointmentId);
    if (success) {
      setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
    }
    return success;
  };

  const handleSaveSettings = async (newSettings) => {
    setSaving(true);
    const success = await saveSettings(newSettings);
    if (success) {
      setSettings(newSettings);
    }
    setSaving(false);
    return success;
  };

  const generateTimeSlots = (startTime, endTime, duration = 15) => {
    const slots = [];
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    
    while (start < end) {
      const timeString = start.toTimeString().slice(0, 5);
      slots.push(timeString);
      start.setMinutes(start.getMinutes() + duration);
    }
    
    return slots;
  };

  const getDaysOfWeek = (weekNum = 0) => {
    const days = [];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekNum * 7));
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  const isSlotAvailable = (date, time) => {
    const dayOfWeek = date.getDay();
    const schedule = settings.weeklySchedule[dayOfWeek];
    
    if (!schedule.available) return false;

   // Controlla se è un giorno festivo
if (isItalianHoliday(date)) return false;

// Controlla se la data è bloccata
const checkDateString = formatDateLocal(date);
const isBlocked = blockedDates.some(blocked => blocked.date === checkDateString);
if (isBlocked) return false;
    
    const timeInRange = schedule.slots.some(slot => {
      const slotTimes = generateTimeSlots(slot.start, slot.end, settings.appointmentDuration);
      return slotTimes.includes(time);
    });
    
    if (!timeInRange) return false;
    
    const dateString = formatDateLocal(date);
    const isBooked = appointments.some(apt =>
      apt.date === dateString && apt.time === time
    );
    
    if (isBooked) return false;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const slotDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (slotDate.getTime() === today.getTime()) {
      const slotDateTime = new Date(date);
      const [hours, minutes] = time.split(':');
      slotDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      const minAllowedTime = new Date(now.getTime() + settings.minAdvanceHours * 60 * 60 * 1000);
      
      return slotDateTime >= minAllowedTime;
    }
    
    return true;
  };

  const isDateInFuture = (date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (compareDate > today) return true;
    if (compareDate.getTime() === today.getTime()) {
      return true;
    }
    
    return false;
  };

  const handleSlotClick = (date, time) => {
    if (!isSlotAvailable(date, time) || !isDateInFuture(date)) return;
    setSelectedSlot({ date, time });
    setShowForm(true);
  };

  const updateSettings = (newSettings) => {
    setSettings({ ...settings, ...newSettings });
  };

  const updateDaySchedule = (dayOfWeek, newSchedule) => {
    setSettings(prev => ({
      ...prev,
      weeklySchedule: {
        ...prev.weeklySchedule,
        [dayOfWeek]: newSchedule
      }
    }));
  };

  const isVaccinationPeriod = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // getMonth() restituisce 0-11, quindi +1 per 1-12
  
  // Periodo vaccinazioni: settembre (9), ottobre (10), novembre (11), dicembre (12)
  return currentMonth >= 9 && currentMonth <= 12;
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Connessione Firebase...</p>
          <p className="text-sm text-gray-400 mt-2">ped-booking</p>
        </div>
      </div>
    );
  }

if (showVaccinationForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto py-12 px-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="mb-6 text-center">
  <h2 className="text-xl font-semibold mb-2">Richiesta Vaccinazione Antinfluenzale</h2>
  <p className="text-sm text-gray-600">(Inserire il nominativo del/la bambino/a)</p>
</div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={vaccinationFormData.nome}
                  onChange={(e) => setVaccinationFormData({...vaccinationFormData, nome: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cognome</label>
                <input
                  type="text"
                  value={vaccinationFormData.cognome}
                  onChange={(e) => setVaccinationFormData({...vaccinationFormData, cognome: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cellulare</label>
                <input
                  type="tel"
                  value={vaccinationFormData.cellulare}
                  onChange={(e) => setVaccinationFormData({...vaccinationFormData, cellulare: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
  <label className="flex items-center">
    <input
      type="checkbox"
      checked={vaccinationFormData.primaVaccinazione}
      onChange={(e) => setVaccinationFormData({...vaccinationFormData, primaVaccinazione: e.target.checked})}
      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
    />
    <span className="text-sm font-medium text-gray-700">Mai vaccinato in precedenza per l'influenza</span>
  </label>
  <p className="text-xs text-gray-500 mt-1 ml-6">Seleziona se è la prima volta che ricevi la vaccinazione antinfluenzale</p>
</div>
            </div>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowVaccinationForm(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                disabled={!vaccinationFormData.nome.trim() || !vaccinationFormData.cognome.trim()}
                onClick={async () => {
  const success = await saveVaccination({
    nome: vaccinationFormData.nome.toLowerCase().trim(),
    cognome: vaccinationFormData.cognome.toLowerCase().trim(),
    cellulare: vaccinationFormData.cellulare,
    primaVaccinazione: vaccinationFormData.primaVaccinazione
  });
  
  if (success.success) {
    // Aggiungi alla lista locale
    setVaccinations(prev => [...prev, { 
      id: success.id, 
      nome: vaccinationFormData.nome.toLowerCase().trim(),
      cognome: vaccinationFormData.cognome.toLowerCase().trim(),
      cellulare: vaccinationFormData.cellulare,
      primaVaccinazione: vaccinationFormData.primaVaccinazione
    }]);
    
    // Pulisci il form
    setVaccinationFormData({ nome: '', cognome: '', cellulare: '', primaVaccinazione: false });
    setShowVaccinationForm(false);
    alert('Richiesta vaccinazione inviata con successo!');
  } else {
    alert('Errore nel salvataggio. Riprova.');
  }
}}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
              >
                Invia Richiesta
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'welcome') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="mb-8">
            <div className="w-32 h-32 flex items-center justify-center mx-auto mb-4">
              <img
                src="/logo-aft-v2.svg"
                alt="Logo"
                className="w-32 h-32 object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
  {process.env.REACT_APP_DOCTOR_NAME}
</h1>

{/* NUOVA SEZIONE INDIRIZZO E TELEFONO */}
<div className="space-y-2 mb-2">
  <div className="flex items-center justify-center text-gray-600">
    <MapPin className="w-4 h-4 mr-2" />
    <a
      href={`https://maps.google.com/?q=${encodeURIComponent(process.env.REACT_APP_DOCTOR_ADDRESS)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-green-600 transition-colors"
    >
      {process.env.REACT_APP_DOCTOR_ADDRESS}
    </a>
  </div>
  <div className="flex items-center justify-center text-gray-600">
    <Phone className="w-4 h-4 mr-2" />
    <span>{process.env.REACT_APP_DOCTOR_PHONE}</span>
  </div>
</div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-semibold text-gray-900">Appuntamento</h2>
                <div className="flex items-center text-gray-500 mt-1">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{settings.appointmentDuration} minuti</span>
                </div>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              Prenota la visita dal tuo Pediatra
            </p>
          </div>
          
          <div className="space-y-4 max-w-sm mx-auto">
  <button 
    onClick={() => {
      setCurrentView('booking');
      setShowVaccinationForm(false);
    }}
    className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-medium transition-colors w-full flex items-center justify-center"
  >
    <Stethoscope className="w-5 h-5 mr-3" />
    Prenota Appuntamento
  </button>
  
  {isVaccinationPeriod() && (
    <button 
      onClick={() => {
        setShowVaccinationForm(true);
        setCurrentView('welcome');
      }}
      className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors w-full flex items-center justify-center"
    >
      <Shield className="w-5 h-5 mr-3" />
      Prenota Vaccinazione Antinfluenzale
    </button>
  )}
</div>

<details className="max-w-sm mx-auto mt-6 text-left bg-green-50 border border-green-200 rounded-lg">
  <summary className="cursor-pointer select-none px-4 py-3 font-medium text-green-800">
    📲 Salva l'app sul telefono
  </summary>
  <div className="px-4 pb-4 text-sm text-gray-700 space-y-3">
    <p className="text-gray-500">
      Aggiungi l'icona alla schermata Home per riaprire l'app con un tocco, senza dover ritrovare il link.
    </p>
    <div>
      <p className="font-semibold text-gray-800">📱 iPhone / iPad</p>
      <p>Apri questa pagina in <strong>Safari</strong>, tocca <strong>Condividi</strong> (il quadrato con la freccia ↑), poi <strong>«Aggiungi a Home»</strong>.</p>
    </div>
    <div>
      <p className="font-semibold text-gray-800">🤖 Android</p>
      <p>Apri questa pagina in <strong>Chrome</strong>, tocca il menu <strong>⋮</strong> in alto a destra, poi <strong>«Aggiungi a schermata Home»</strong> (oppure «Installa app»).</p>
    </div>
  </div>
</details>

{cameFromAdmin && (
  <button
    onClick={() => {
      setCameFromAdmin(false);
      setCurrentView('admin');
    }}
    className="bg-gray-500 hover:bg-gray-600 text-white px-8 py-3 rounded-lg font-medium transition-colors w-full mt-4 max-w-sm mx-auto"
  >
    Area Amministratore
  </button>
)}
                    
        </div>
      </div>
    );
  }

  if (currentView === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="max-w-md mx-auto">
          {/* Messaggio successo */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Prenotazione Confermata!
            </h1>
            <p className="text-gray-600 text-sm">
              {/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
                ? 'Salva o condividi il promemoria usando il menu sotto'
                : 'Lo screenshot è stato scaricato automaticamente'}
            </p>
          </div>

          {/* Card visibile con dettagli - stessa di quella nello screenshot */}
          {confirmedAppointment && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="text-center space-y-4">
                <div className="pb-4 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-green-600 mb-1">
                    ✓ Prenotazione Confermata
                  </h2>
                </div>

                <div className="space-y-3 py-2">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">📅 Data</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {confirmedAppointment.dateFormatted}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-1">🕐 Orario</div>
                    <div className="text-2xl font-bold text-green-600">
                      {confirmedAppointment.time}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Elemento nascosto per screenshot - identico al card visibile */}
          {confirmedAppointment && (
            <div
              ref={screenshotRef}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '400px',
                backgroundColor: '#ffffff'
              }}
            >
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="text-center space-y-4">
                  <div className="pb-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-green-600 mb-1">
                      ✓ Prenotazione Confermata
                    </h2>
                  </div>

                  <div className="space-y-3 py-2">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">📅 Data</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {confirmedAppointment.dateFormatted}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">🕐 Orario</div>
                      <div className="text-2xl font-bold text-green-600">
                        {confirmedAppointment.time}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pulsanti */}
          <div className="space-y-3">
            <button
              onClick={generateScreenshot}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
                ? '📤 Salva o Condividi'
                : '📥 Scarica di nuovo'}
            </button>

            <button
              onClick={() => {
                if (!confirmedAppointment) return;
                const ics = buildAppointmentIcs({
                  dateString: confirmedAppointment.date,
                  time: confirmedAppointment.time,
                  title: process.env.REACT_APP_DOCTOR_NAME || 'Appuntamento pediatrico'
                });
                downloadIcs('appuntamento.ics', ics);
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              📅 Aggiungi al calendario
            </button>

            <button
              onClick={() => setCurrentView('welcome')}
              className="w-full bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Torna alla home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'booking') {
    if (showForm) {
      const motiviVisite = [
        'Febbre', 'Tosse', 'Raffreddore', 'Mal di gola', 
        'Vomito', 'Diarrea', 'Altro', 'Controllo generale periodico'
      ];

      return (
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-4xl mx-auto py-8 px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Calendar className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Appuntamento</h3>
                      <div className="flex items-center text-gray-500 text-sm">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{settings.appointmentDuration} minuti</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-600 mb-2">Data e ora:</p>
                    <p className="font-medium">{selectedSlot?.date?.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <p className="font-medium">{selectedSlot?.time}</p>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    Prenota la visita dal tuo Pediatra
                  </p>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold mb-6">Inserisci i dati del/la bambino/a</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cognome</label>
                      <input
                        type="text"
                        value={formData.cognome}
                        onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cellulare</label>
                    <input
                      type="tel"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Motivo della visita</label>
                    <div className="grid grid-cols-2 gap-2">
                      {motiviVisite.map((motivo) => (
                        <label key={motivo} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.motivo.includes(motivo)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({...formData, motivo: [...formData.motivo, motivo]});
                              } else {
                                setFormData({...formData, motivo: formData.motivo.filter(m => m !== motivo)});
                              }
                            }}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{motivo}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annotazioni aggiuntive <span className="text-gray-400 font-normal">Opzionale</span>
                    </label>
                    <textarea
                      value={formData.note}
                      onChange={(e) => setFormData({...formData, note: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      rows="3"
                    />
                  </div>

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Indietro
                    </button>
                    <button
                      type="button"
                      disabled={!formData.nome.trim() || !formData.cognome.trim() || saving}
                      onClick={async () => {
                        if (!selectedSlot) return;
                        
                        const appointmentData = {
                          date: formatDateLocal(selectedSlot.date),
                          time: selectedSlot.time,
                          nome: formData.nome.toLowerCase().trim(),
                          cognome: formData.cognome.toLowerCase().trim(),
                          email: formData.email,
                          motivo: formData.motivo,
                          note: formData.note,
                          createdAt: new Date().toISOString()
                        };

                        const result = await handleSaveAppointment(appointmentData, { enforceSlotUnique: true });

                        if (result.reason === 'slot_taken') {
                          alert('Spiacenti, questo orario è stato appena prenotato da un altro paziente. Scegli un altro slot disponibile.');
                          setSelectedSlot(null);
                          setShowForm(false);
                          return;
                        }

                        if (result.success) {
                          // Salva i dati per lo screenshot
                          setConfirmedAppointment({
                            ...appointmentData,
                            dateFormatted: selectedSlot.date.toLocaleDateString('it-IT', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          });
                          setFormData({ nome: '', cognome: '', email: '', motivo: [], note: '' });
                          setSelectedSlot(null);
                          setShowForm(false);
                          setCurrentView('success');
                        } else {
                          alert('Errore nel salvataggio. Riprova.');
                        }
                      }}
                      className="px-8 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors flex items-center"
                    >
                      {saving ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Salvando in Firebase...
                        </>
                      ) : (
                        'Conferma Prenotazione'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Resto del componente booking (calendario)
    const actualWeekIndex = weekOffset + currentWeek;
    const weekDays = getDaysOfWeek(actualWeekIndex);
    const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

    const weekButtons = [];
    for (let i = 0; i < 4; i++) {
      const weekIndex = weekOffset + i;
      let label;
      
      if (weekIndex === 0) {
        label = "Questa Settimana";
      } else if (weekIndex === 1) {
        label = "La Prossima Settimana";
      } else {
        const days = getDaysOfWeek(weekIndex);
        const monday = days[1];
        const sunday = days[6];
        
        if (monday.getMonth() === sunday.getMonth()) {
          label = `${monday.getDate()} - ${sunday.getDate()} ${monday.toLocaleDateString('it-IT', { month: 'long' })}`;
        } else {
          label = `${monday.getDate()} ${monday.toLocaleDateString('it-IT', { month: 'short' })} - ${sunday.getDate()} ${sunday.toLocaleDateString('it-IT', { month: 'short' })}`;
        }
      }
      
      weekButtons.push({ index: i, label: label });
    }

    let headerLabel;
    if (actualWeekIndex === 0) {
      headerLabel = "QUESTA SETTIMANA";
    } else if (actualWeekIndex === 1) {
      headerLabel = "LA PROSSIMA SETTIMANA";
    } else {
      const days = getDaysOfWeek(actualWeekIndex);
      const monday = days[0];
const friday = days[4];
headerLabel = `${monday.getDate()} ${monday.toLocaleDateString('it-IT', { month: 'short' }).toUpperCase()} - ${friday.getDate()} ${friday.toLocaleDateString('it-IT', { month: 'short' }).toUpperCase()}`;
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-4 px-4">
          <div className="mb-6">
            <button
              onClick={() => setCurrentView('welcome')}
              className="text-green-600 hover:text-green-700 mb-4"
            >
              ← Torna indietro
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Scegli un orario</h1>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
            <div className="w-full lg:w-80 order-2 lg:order-1">
              <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <Calendar className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm lg:text-base">Appuntamento</h3>
                    <div className="flex items-center text-gray-500 text-xs lg:text-sm">
                      <Clock className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                      <span>{settings.appointmentDuration} minuti</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs lg:text-sm text-gray-600 mb-4 lg:mb-6">
                  Prenota la visita dal tuo Pediatra
                </p>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 text-sm lg:text-base">Navigazione</h4>
                  <div className="space-y-2">
                    {weekButtons.map((week) => (
                      <button
                        key={week.index}
                        onClick={() => setCurrentWeek(week.index)}
                        className={`w-full text-left p-2 lg:p-3 rounded-lg border transition-colors text-xs lg:text-sm ${
                          currentWeek === week.index 
                            ? 'bg-green-100 border-green-300 text-green-700 font-medium' 
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {week.label}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => {
                        setWeekOffset(weekOffset + 4);
                        setCurrentWeek(0);
                      }}
                      className="w-full text-center p-2 lg:p-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors text-xs lg:text-sm font-medium"
                    >
                      ► Successive
                    </button>
                    
                    {weekOffset > 0 && (
                      <button
                        onClick={() => {
                          setWeekOffset(Math.max(0, weekOffset - 4));
                          setCurrentWeek(0);
                        }}
                        className="w-full text-center p-2 lg:p-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors text-xs lg:text-sm"
                      >
                        ◄ Precedenti
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 order-1 lg:order-2">
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-3 lg:p-4 border-b">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <h3 className="font-semibold text-gray-900 text-sm lg:text-base">
                      {headerLabel}
                    </h3>
                    <div className="text-xs lg:text-sm text-gray-500">
                      Attualmente {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} Europe/Rome
                    </div>
                  </div>
                </div>
                
                <div className="p-3 lg:p-6">
                  {weekDays.map((day) => {
                    const dayOfWeek = day.getDay();
                    const schedule = settings.weeklySchedule[dayOfWeek];
                    const isInFuture = isDateInFuture(day);
                    
                    if (!schedule.available) {
                      return null;
                    }

                    const allSlots = schedule.slots.flatMap(slot => 
                      generateTimeSlots(slot.start, slot.end, settings.appointmentDuration)
                    );

                    return (
                      <div key={day.toISOString()} className="mb-4 lg:mb-6">
                        <h4 className="font-medium text-gray-900 mb-2 lg:mb-3 text-sm lg:text-base">
                          {dayNames[dayOfWeek]} {day.getDate()} {day.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                        </h4>
                        
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                          {allSlots.map((time) => {
                            const isAvailable = isSlotAvailable(day, time) && isInFuture;
                            
                            return (
                              <button
                                key={time}
                                onClick={() => handleSlotClick(day, time)}
                                disabled={!isAvailable}
                                className={`px-2 py-2 lg:px-3 lg:py-2 rounded text-xs lg:text-sm font-medium transition-colors ${
                                  isAvailable 
                                    ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 cursor-pointer active:bg-green-200' 
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                              >
                                {time}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'admin') {
    const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

    return (
      <div className="min-h-screen bg-gray-50">
  <div className="max-w-[1400px] mx-auto py-8 px-4">
          <div className="mb-8">
            <button
              onClick={() => {
  setCameFromAdmin(true);
  setCurrentView('welcome');
}}
              className="text-green-600 hover:text-green-700 mb-4"
            >
              ← Torna alla home
            </button>
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Area Amministratore</h1>
              <div className="flex items-center text-sm text-green-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                Firebase connesso
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200">
            <div className="border-b">
              <nav className="flex">
                <button 
                  onClick={() => setActiveAdminTab('appointments')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 ${
                    activeAdminTab === 'appointments' 
                      ? 'border-green-500 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Appuntamenti ({appointments.length})
                </button>
                <button 
  onClick={() => setActiveAdminTab('calendar')}
  className={`px-6 py-4 text-sm font-medium border-b-2 ${
    activeAdminTab === 'calendar' 
      ? 'border-green-500 text-green-600' 
      : 'border-transparent text-gray-500 hover:text-gray-700'
  }`}
>
  <Calendar className="w-4 h-4 inline mr-2" />
  Calendario
</button>
                <button 
                  onClick={() => setActiveAdminTab('settings')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 ${
                    activeAdminTab === 'settings' 
                      ? 'border-green-500 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >                
                  <Settings className="w-4 h-4 inline mr-2" />
                  Impostazioni
                </button>
                <button 
  onClick={() => setActiveAdminTab('vaccinations')}
  className={`px-6 py-4 text-sm font-medium border-b-2 ${
    activeAdminTab === 'vaccinations' 
      ? 'border-green-500 text-green-600' 
      : 'border-transparent text-gray-500 hover:text-gray-700'
  }`}
>
  Vaccinazioni ({vaccinations.length})
</button>
              </nav>
            </div>

            <div className="p-6">
            {activeAdminTab === 'calendar' && (
  <div className="space-y-6">
    {/* Header con navigazione */}
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => setCalendarDate(new Date())}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
        >
          Oggi
        </button>
        <button
          onClick={() => {
            const prevMonth = new Date(calendarDate);
            prevMonth.setMonth(calendarDate.getMonth() - 1);
            setCalendarDate(prevMonth);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={() => {
            const nextMonth = new Date(calendarDate);
            nextMonth.setMonth(calendarDate.getMonth() + 1);
            setCalendarDate(nextMonth);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>
      <h2 className="text-xl font-semibold text-gray-900">
        {calendarDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
      </h2>
    </div>

    {/* Calendario */}
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header giorni della settimana */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'].map(day => (
          <div key={day} className="px-3 py-2 bg-gray-50 text-center text-xs font-medium text-gray-600 border-r border-gray-200 last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      {/* Griglia calendario */}
      <div className="grid grid-cols-7 min-h-[600px]">
        {(() => {
          const year = calendarDate.getFullYear();
          const month = calendarDate.getMonth();
          const firstDay = new Date(year, month, 1);
          const startDate = new Date(firstDay);
          startDate.setDate(startDate.getDate() - (firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1));
          
          const days = [];
          const currentDate = new Date(startDate);
          
          for (let i = 0; i < 42; i++) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          return days.map((day, index) => {
            const isCurrentMonth = day.getMonth() === month;
            const isToday = day.toDateString() === new Date().toDateString();
            const dayOfWeek = day.getDay();
            const schedule = settings.weeklySchedule[dayOfWeek];
            
            // Ottieni appuntamenti per questo giorno
            const dayString = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
            const dayAppointments = appointments.filter(apt => apt.date === dayString);
            
            // Genera tutti gli slot disponibili per questo giorno
const availableSlots = [];
const isHoliday = isItalianHoliday(day);
if (schedule && schedule.available && !isHoliday) {
  schedule.slots.forEach(slot => {
    const timeSlots = generateTimeSlots(slot.start, slot.end, settings.appointmentDuration);
    availableSlots.push(...timeSlots);
  });
}

// Per weekend e festivi, aggiungi un singolo slot per eventi privati
if (!schedule || !schedule.available || isHoliday) {
  // Un solo slot generico per eventi privati nei giorni chiusi
  availableSlots.push('09:00');
}
            
            return (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b border-gray-200 p-2 ${
                  isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                } ${isToday ? 'bg-blue-50' : ''}`}
              >
                <div className={`text-sm font-medium mb-2 ${
  isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
} ${isToday ? 'text-blue-600' : ''} ${isItalianHoliday(day) ? 'text-red-600' : ''}`}>
  <div className="flex justify-between items-center">
    <span>{day.getDate()}</span>
    {isCurrentMonth && schedule && schedule.available && !isItalianHoliday(day) && (
      <button
        onClick={() => {
          const dayString = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
          const isCurrentlyBlocked = blockedDates.some(blocked => blocked.date === dayString);
          const hasAppointments = dayAppointments.length > 0;
          
          if (isCurrentlyBlocked) {
            // Sblocca
            if (window.confirm(`Sbloccare la giornata del ${day.getDate()}/${day.getMonth() + 1}?`)) {
              removeBlockedDate(dayString).then(success => {
                if (success) {
                  setBlockedDates(prev => prev.filter(blocked => blocked.date !== dayString));
                }
              });
            }
          } else {
            // Blocca solo se non ci sono appuntamenti
            if (hasAppointments) {
              alert('Impossibile bloccare: ci sono già appuntamenti prenotati in questo giorno');
            } else {
              if (window.confirm(`Bloccare tutti gli slot del ${day.getDate()}/${day.getMonth() + 1}? Gli slot non saranno più disponibili al pubblico.`)) {
                saveBlockedDate(dayString, 'Bloccato manualmente').then(result => {
                  if (result.success) {
                    setBlockedDates(prev => [...prev, { id: dayString, date: dayString, reason: 'Bloccato manualmente' }]);
                  }
                });
              }
            }
          }
        }}
        className={`text-xs px-1 py-0.5 rounded ${
          blockedDates.some(blocked => blocked.date === `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`)
            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        title={
          blockedDates.some(blocked => blocked.date === `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`)
            ? 'Clicca per sbloccare' 
            : 'Clicca per bloccare'
        }
      >
        {blockedDates.some(blocked => blocked.date === `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`) ? '🔓' : '🔒'}
      </button>
    )}
  </div>
  {isItalianHoliday(day) && <span className="text-xs block text-red-500">Festivo</span>}
</div>
                
                {(
                  <div className="space-y-1">
  {/* Unisci e ordina tutti gli slot */}
  {(() => {
    // Crea array con tutti gli slot (prenotati + disponibili)
    const allSlots = [
      // Slot prenotati
      ...dayAppointments.map(apt => ({ type: 'booked', time: apt.time, data: apt })),
      // Slot disponibili
      ...availableSlots
        .filter(slot => !dayAppointments.some(apt => apt.time === slot))
        .map(slot => ({ type: 'available', time: slot }))
    ];
    
    // Ordina tutti per orario
    allSlots.sort((a, b) => a.time.localeCompare(b.time));
    
    return allSlots.map((slot, index) => {
      if (slot.type === 'booked') {
        const apt = slot.data;
        return (
          <div
  key={apt.id}
  className={`text-xs px-2 py-1 rounded cursor-pointer transition-colors ${
    apt.isPrivate 
      ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' 
      : 'bg-green-100 text-green-800 hover:bg-green-200'
  }`}
  title={`${apt.time} - ${apt.nome} ${apt.cognome}${apt.motivo?.length ? ' - ' + apt.motivo.join(', ') : ''}${apt.isPrivate ? ' (Evento privato)' : ''} - Clicca per eliminare`}
  onClick={async () => {
    if (window.confirm(`Eliminare l'appuntamento di ${apt.nome} ${apt.cognome} del ${new Date(apt.date).toLocaleDateString('it-IT')} alle ${apt.time}?`)) {
      const success = await handleDeleteAppointment(apt.id);
      if (success) {
        alert('Appuntamento eliminato con successo');
      } else {
        alert('Errore nell\'eliminazione');
      }
    }
  }}
>
            <div className="truncate">
              <span className="font-medium">{apt.time}</span> {apt.cognome} {apt.nome}
              {apt.isPrivate && <span className="ml-1 text-orange-600">🔒</span>}
            </div>
          </div>
        );
      } else {
        // Controlla se questo giorno è bloccato
                        const dayString = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                        const isDayBlocked = blockedDates.some(blocked => blocked.date === dayString);
                        
                        return (
                          <div
                            key={slot.time}
                            className={`text-xs px-2 py-1 rounded cursor-pointer transition-colors ${
                              isDayBlocked 
                                ? 'bg-red-50 text-red-600 border border-red-200 line-through cursor-not-allowed' 
                                : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700'
                            }`}
                            onClick={() => {
                              if (!isDayBlocked) {
                                setSelectedCalendarSlot({ date: day, time: slot.time });
                                setCalendarFormData({ nome: '', cognome: '', email: '', motivo: [], note: '', customTime: '', isPrivate: false });
                                setShowCalendarForm(true);
                              }
                            }}
                            title={isDayBlocked ? `Slot bloccato: ${slot.time}` : `Slot disponibile: ${slot.time}`}
                          >
                            {slot.time}
                          </div>
                        );
      }
    });
  })()}
</div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>

    {/* Legenda */}
    <div className="flex items-center justify-center space-x-6 text-sm">
      <div className="flex items-center">
        <div className="w-3 h-3 bg-green-100 rounded mr-2"></div>
        <span className="text-gray-600">Appuntamento prenotato</span>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 bg-gray-100 rounded mr-2"></div>
        <span className="text-gray-600">Slot disponibile (clicca per prenotare)</span>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 bg-blue-50 border border-blue-200 rounded mr-2"></div>
        <span className="text-gray-600">Oggi</span>
      </div>
    </div>

{showCalendarForm && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">
          Nuovo Appuntamento
        </h3>
        <button
          onClick={() => setShowCalendarForm(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <Trash2 className="w-6 h-6" />
        </button>
      </div>

      {selectedCalendarSlot && (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
    <div className="flex items-center">
      <Calendar className="w-5 h-5 text-blue-600 mr-2" />
      <span className="font-medium text-blue-900">
        {selectedCalendarSlot.date.toLocaleDateString('it-IT', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })} alle {calendarFormData.customTime || selectedCalendarSlot.time}
      </span>
    </div>
    
    <div className="mt-4 pt-4 border-t border-blue-200">
      <label className="block text-sm font-medium text-blue-800 mb-2">
        Orario personalizzato (solo admin)
      </label>
      <input
        type="time"
        value={calendarFormData.customTime || selectedCalendarSlot.time}
        onChange={(e) => setCalendarFormData({...calendarFormData, customTime: e.target.value})}
        className="border border-blue-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
      />
      <p className="text-xs text-blue-600 mt-1">
        Modifica l'orario per appuntamenti in slot non pubblici
      </p>
    </div>
  </div>
)}

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
            <input
              type="text"
              value={calendarFormData.nome}
              onChange={(e) => setCalendarFormData({...calendarFormData, nome: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cognome *</label>
            <input
              type="text"
              value={calendarFormData.cognome}
              onChange={(e) => setCalendarFormData({...calendarFormData, cognome: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Cellulare (opzionale)</label>
          <input
            type="tel"
            value={calendarFormData.email}
            onChange={(e) => setCalendarFormData({...calendarFormData, email: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Motivo della visita (opzionale)</label>
          <div className="grid grid-cols-2 gap-2">
            {['Febbre', 'Tosse', 'Raffreddore', 'Mal di gola', 'Vomito', 'Diarrea', 'Altro', 'Controllo generale periodico'].map((motivo) => (
              <label key={motivo} className="flex items-center">
                <input
                  type="checkbox"
                  checked={calendarFormData.motivo.includes(motivo)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setCalendarFormData({...calendarFormData, motivo: [...calendarFormData.motivo, motivo]});
                    } else {
                      setCalendarFormData({...calendarFormData, motivo: calendarFormData.motivo.filter(m => m !== motivo)});
                    }
                  }}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="ml-2 text-sm text-gray-700">{motivo}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Annotazioni aggiuntive <span className="text-gray-400 font-normal">(opzionale)</span>
          </label>
          <textarea
            value={calendarFormData.note}
            onChange={(e) => setCalendarFormData({...calendarFormData, note: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            rows="3"
          />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={calendarFormData.isPrivate}
              onChange={(e) => setCalendarFormData({...calendarFormData, isPrivate: e.target.checked})}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-3"
            />
            <span className="text-sm font-medium text-gray-700">Evento privato</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Gli eventi privati sono visibili solo nell'area amministratore e possono essere creati anche nei weekend/festivi
          </p>
        </div>
      </div>

      <div className="flex justify-between mt-6 pt-4 border-t">
        <button
          onClick={() => setShowCalendarForm(false)}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Annulla
        </button>
        <button
          disabled={!calendarFormData.nome.trim() || !calendarFormData.cognome.trim() || saving}
          onClick={async () => {
            if (!selectedCalendarSlot) return;
            
            const appointmentData = {
              date: formatDateLocal(selectedCalendarSlot.date),
              time: calendarFormData.customTime || selectedCalendarSlot.time,
              nome: calendarFormData.isPrivate ? calendarFormData.nome.trim() : calendarFormData.nome.toLowerCase().trim(),
              cognome: calendarFormData.isPrivate ? calendarFormData.cognome.trim() : calendarFormData.cognome.toLowerCase().trim(),
              email: calendarFormData.email,
              motivo: calendarFormData.motivo,
              note: calendarFormData.note,
              isPrivate: calendarFormData.isPrivate,
              createdAt: new Date().toISOString()
            };

            const result = await handleSaveAppointment(appointmentData);

            if (result.success) {
              setCalendarFormData({ nome: '', cognome: '', email: '', motivo: [], note: '', customTime: '', isPrivate: false });
              setSelectedCalendarSlot(null);
              setShowCalendarForm(false);
              alert('Appuntamento creato con successo!');
            } else {
              alert('Errore nel salvataggio. Riprova.');
            }
          }}
          className="px-8 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors flex items-center"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            'Crea Appuntamento'
          )}
        </button>
      </div>
    </div>
  </div>
)}

  </div>
)}
  {activeAdminTab === 'appointments' && (
    <>
      {appointments.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">Nessun appuntamento in Firebase</p>
          <p className="text-sm text-gray-400">I nuovi appuntamenti appariranno qui automaticamente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(() => {
  // Funzione per ottenere l'inizio della settimana
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Lunedì come primo giorno
    return new Date(d.setDate(diff));
  };

  // Funzione per formattare la settimana
  const formatWeek = (weekStart) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const today = new Date();
    const thisWeekStart = getWeekStart(today);
    
    // Controlla se è questa settimana
    if (weekStart.getTime() === thisWeekStart.getTime()) {
      return "QUESTA SETTIMANA";
    }
    
    // Controlla se è la prossima settimana
    const nextWeekStart = new Date(thisWeekStart);
    nextWeekStart.setDate(thisWeekStart.getDate() + 7);
    if (weekStart.getTime() === nextWeekStart.getTime()) {
      return "PROSSIMA SETTIMANA";
    }
    
    // Altrimenti mostra le date
    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${weekStart.getDate()}-${weekEnd.getDate()} ${weekStart.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`;
    } else {
      return `${weekStart.getDate()} ${weekStart.toLocaleDateString('it-IT', { month: 'short' })} - ${weekEnd.getDate()} ${weekEnd.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })}`;
    }
  };

  // Filtra appuntamenti futuri (da oggi in poi)
const today = new Date();
const todayString = formatDateLocal(today);

const futureAppointments = appointments.filter(appointment => {
  return appointment.date >= todayString;
});

// Se non ci sono appuntamenti futuri, mostra messaggio
if (futureAppointments.length === 0) {
  return (
    <div className="text-center py-8">
      <p className="text-gray-500 mb-2">Nessun appuntamento futuro</p>
      <p className="text-sm text-gray-400">
        Gli appuntamenti passati sono nascosti da questa vista ma restano visibili nel Calendario
      </p>
      <p className="text-xs text-gray-400 mt-2">
        Totale appuntamenti in archivio: {appointments.length}
      </p>
    </div>
  );
}

  // Raggruppa appuntamenti per settimana
  const groupedAppointments = futureAppointments
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return a.time.localeCompare(b.time);
    })
    .reduce((groups, appointment) => {
      const appointmentDate = new Date(appointment.date);
      const weekStart = getWeekStart(appointmentDate);
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!groups[weekKey]) {
        groups[weekKey] = {
          weekStart: weekStart,
          appointments: []
        };
      }
      
      groups[weekKey].appointments.push(appointment);
      return groups;
    }, {});

  return Object.keys(groupedAppointments)
    .sort()
    .map(weekKey => {
      const group = groupedAppointments[weekKey];
      const weekLabel = formatWeek(group.weekStart);
      
      return (
        <div key={weekKey} className="mb-8">
          <div className="flex items-center mb-4">
            <div className="flex-1 border-t border-gray-200"></div>
            <div className="px-4 py-2 bg-gray-100 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {weekLabel} ({group.appointments.length})
              </h3>
            </div>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>
          
          <div className="space-y-3">
            {group.appointments.map((appointment) => (
              <div key={appointment.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="font-medium">{appointment.cognome} {appointment.nome}</span>
                      {appointment.isPrivate && (
                        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                          Evento privato
                        </span>
                      )}
                    </div>
                    <div className="flex items-center mb-2">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{new Date(appointment.date).toLocaleDateString('it-IT')} alle {appointment.time}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{appointment.email}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 mr-2" />
                      <span>Motivo: {appointment.motivo.join(', ')}</span>
                    </div>
                    {appointment.note && (
                      <div className="text-sm text-gray-600 mt-2">
                        Note: {appointment.note}
                      </div>
                    )}
                    <div className="text-xs text-green-600 mt-2 font-mono">
                      Firebase ID: {appointment.id}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleDeleteAppointment(appointment.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Elimina da Firebase"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    });
})()}
        </div>
      )}
    </>
  )}

  {activeAdminTab === 'settings' && (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Impostazioni Generali</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Durata appuntamento (minuti)
            </label>
            <select
              value={settings.appointmentDuration}
              onChange={(e) => updateSettings({ appointmentDuration: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value={15}>15 minuti</option>
              <option value={20}>20 minuti</option>
              <option value={30}>30 minuti</option>
              <option value={45}>45 minuti</option>
              <option value={60}>60 minuti</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preavviso minimo (ore)
            </label>
            <select
              value={settings.minAdvanceHours}
              onChange={(e) => updateSettings({ minAdvanceHours: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value={0}>Nessun preavviso</option>
              <option value={1}>1 ora</option>
              <option value={2}>2 ore</option>
              <option value={4}>4 ore</option>
              <option value={24}>24 ore</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prenotazione massima (giorni)
            </label>
            <select
              value={settings.maxAdvanceDays}
              onChange={(e) => updateSettings({ maxAdvanceDays: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value={30}>30 giorni</option>
              <option value={60}>60 giorni</option>
              <option value={90}>90 giorni</option>
              <option value={180}>180 giorni</option>
              <option value={365}>365 giorni</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Orari Settimanali</h3>
        <div className="space-y-4">
          {dayNames.map((dayName, index) => {
            const dayOfWeek = index;
            const daySchedule = settings.weeklySchedule[dayOfWeek];
            
            return (
              <div key={dayOfWeek} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{dayName}</h4>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={daySchedule.available}
                      onChange={(e) => updateDaySchedule(dayOfWeek, {
                        ...daySchedule,
                        available: e.target.checked,
                        slots: e.target.checked ? daySchedule.slots : []
                      })}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-2"
                    />
                    <span className="text-sm text-gray-600">Disponibile</span>
                  </label>
                </div>
                
                {daySchedule.available && (
                  <div className="space-y-2">
                    {daySchedule.slots.map((slot, slotIndex) => (
                      <div key={slotIndex} className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) => {
                            const newSlots = [...daySchedule.slots];
                            newSlots[slotIndex] = { ...slot, start: e.target.value };
                            updateDaySchedule(dayOfWeek, { ...daySchedule, slots: newSlots });
                          }}
                          className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) => {
                            const newSlots = [...daySchedule.slots];
                            newSlots[slotIndex] = { ...slot, end: e.target.value };
                            updateDaySchedule(dayOfWeek, { ...daySchedule, slots: newSlots });
                          }}
                          className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        {daySchedule.slots.length > 1 && (
                          <button
                            onClick={() => {
                              const newSlots = daySchedule.slots.filter((_, i) => i !== slotIndex);
                              updateDaySchedule(dayOfWeek, { ...daySchedule, slots: newSlots });
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    
                    <button
                      onClick={() => {
                        const newSlots = [...daySchedule.slots, { start: '09:00', end: '10:00' }];
                        updateDaySchedule(dayOfWeek, { ...daySchedule, slots: newSlots });
                      }}
                      className="text-green-600 hover:text-green-700 text-sm font-medium"
                    >
                      + Aggiungi fascia oraria
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={async () => {
            const success = await handleSaveSettings(settings);
            if (success) {
              alert('Impostazioni salvate in Firebase!');
            } else {
              alert('Errore nel salvataggio delle impostazioni');
            }
          }}
          disabled={saving}
          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center"
        >
          {saving ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Salvando...' : 'Salva in Firebase'}
        </button>
      </div>
    </div>
  )}

  {/* AGGIUNGI QUESTO NUOVO BLOCCO QUI */}
  {activeAdminTab === 'vaccinations' && (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Richieste Vaccinazioni Antinfluenzali</h3>
        {vaccinations.length > 0 && (
          <button
            onClick={() => {
              const textToCopy = `RICHIESTE VACCINAZIONI ANTINFLUENZALI
${'='.repeat(45)}
Totale richieste: ${vaccinations.length}

${[...vaccinations].sort((a, b) => {
  const cognomeCompare = a.cognome.localeCompare(b.cognome);
  return cognomeCompare !== 0 ? cognomeCompare : a.nome.localeCompare(b.nome);
}).map((vaccination, index) => 
  `${index + 1}. ${vaccination.cognome} ${vaccination.nome} - ${vaccination.cellulare}${vaccination.primaVaccinazione ? ' - Prima vaccinazione' : ''}`
).join('\n')}

${'='.repeat(45)}
Generato automaticamente da ped-booking`;

              navigator.clipboard.writeText(textToCopy).then(() => {
                alert('Elenco copiato negli appunti!');
              }).catch(() => {
                alert('Errore nella copia. Prova a selezionare manualmente il testo.');
              });
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center text-sm"
          >
            <Mail className="w-4 h-4 mr-2" />
            Copia tutto l'elenco
          </button>
        )}
      </div>

      {vaccinations.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-gray-500 mb-2">Nessuna richiesta di vaccinazione</p>
          <p className="text-sm text-gray-400">Le richieste di vaccinazione appariranno qui automaticamente</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-blue-900">
  {vaccinations.length} richieste totali
</p>
                <p className="text-sm text-blue-700">
  Vaccinazioni antinfluenzali stagione {new Date().getFullYear()}-{new Date().getFullYear() + 1}
</p>
              </div>
            </div>
          </div>

          {[...vaccinations].sort((a, b) => {
  const cognomeCompare = a.cognome.localeCompare(b.cognome);
  return cognomeCompare !== 0 ? cognomeCompare : a.nome.localeCompare(b.nome);
}).map((vaccination) => (
            <div key={vaccination.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
  <User className="w-4 h-4 text-gray-400 mr-2" />
  <span className="font-medium text-lg">{vaccination.cognome} {vaccination.nome}</span>
  {vaccination.primaVaccinazione && (
    <span className="ml-3 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
      Prima vaccinazione
    </span>
  )}
</div>
                  <div className="flex items-center mb-2">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="font-mono">{vaccination.cellulare}</span>
                  </div>
                  <div className="flex items-center mb-2">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm">
                      Richiesta il: {vaccination.createdAt || vaccination.timestamp ? 
                        new Date(vaccination.createdAt || vaccination.timestamp).toLocaleDateString('it-IT', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Data non disponibile'
                      }
                    </span>
                  </div>
                  <div className="text-xs text-blue-600 mt-2 font-mono">
                    Firebase ID: {vaccination.id}
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={async () => {
                      if (window.confirm(`Eliminare la richiesta di ${vaccination.nome} ${vaccination.cognome}?`)) {
                        const success = await deleteVaccination(vaccination.id);
                        if (success) {
                          setVaccinations(prev => prev.filter(v => v.id !== vaccination.id));
                          alert('Richiesta eliminata con successo');
                        } else {
                          alert('Errore nell\'eliminazione');
                        }
                      }
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Elimina richiesta"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Informazioni</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>• Le richieste vengono salvate automaticamente in Firebase</p>
          <p>• Usa "Copia tutto l'elenco" per esportare i dati</p>
          <p>• I dati includono: nome, cognome, cellulare e data della richiesta</p>
          <p>• Il periodo vaccinazioni è attivo da fine agosto a dicembre</p>
        </div>
      </div>
    </div>
  )}
</div>
          </div>
        </div>
      </div>
    );
  }

  return <div>Vista non trovata</div>;
};

export default PediatraBookingApp;
